import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Order, ShippingDetails } from '@/components/profile/order-types';
import { ShippingInfo } from '@/components/profile/OrderActions';
import { useStockManagement } from '@/hooks/useStockManagement';
import { commonToasts } from '@/hooks/toast/toast-translations';

export const useSales = (userId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const { handleIncrementStock } = useStockManagement();
  
  // Fetch sales data
  const { data: sales, isLoading, error, refetch } = useQuery({
    queryKey: ['userSales', userId],
    queryFn: async () => {
      // Get orders where the user is the seller
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id, 
          status, 
          amount, 
          currency, 
          created_at,
          delivery_address,
          shipping_method,
          shipping_details,
          user_id,
          seller_id,
          cancellation_reason,
          requires_confirmation,
          listings(id, title, images, stock)
        `)
        .eq('seller_id', userId)
        .order('created_at', { ascending: false });
      
      if (ordersError) throw ordersError;
      
      if (ordersData && ordersData.length > 0) {
        // Get unique buyer IDs
        const buyerIds = [...new Set(ordersData.map(order => order.user_id))];
        
        // Fetch all relevant buyer profiles in a single query
        const { data: buyerProfiles, error: buyerError } = await supabase
          .from('profiles')
          .select('id, full_name, phone_number, email')
          .in('id', buyerIds);
          
        if (buyerError) throw buyerError;
        
        // Attach buyer profile to each order
        return ordersData.map(order => ({
          ...order,
          buyer_profile: buyerProfiles?.find(profile => profile.id === order.user_id) || null
        })) as Order[];
      }
      
      return ordersData as Order[];
    },
  });
  
  // Update order status mutation
  const updateOrderStatusMutation = useMutation({
    mutationFn: async ({ 
      orderId, 
      newStatus, 
      shippingInfo 
    }: { 
      orderId: string; 
      newStatus: string; 
      shippingInfo?: ShippingInfo 
    }) => {
      setIsUpdating(orderId);
      
      // Prepare update data
      const updateData: any = { 
        status: newStatus,
        updated_at: new Date().toISOString() 
      };
      
      // Add shipping info if provided
      if (shippingInfo && newStatus === 'shipped') {
        updateData.shipping_details = shippingInfo;
      }
      
      // If we're cancelling, check if this is a response to a buyer's cancellation request
      if (newStatus === 'cancelled') {
        // Get the order details first to get the listing_id and current stock
        const { data: orderDetails } = await supabase
          .from('orders')
          .select(`
            requires_confirmation, 
            status, 
            listing_id, 
            listings(stock)
          `)
          .eq('id', orderId)
          .single();
          
        if (orderDetails) {
          // Check if we're responding to a buyer's cancellation request
          if (orderDetails.requires_confirmation && orderDetails.status === 'cancelled') {
            // This is confirming a buyer's cancellation, no need for a seller reason
            updateData.requires_confirmation = false;
          } else {
            // This is a seller-initiated cancellation
            updateData.cancellation_reason = cancellationReason;
            updateData.cancelled_by = userId;
          }
          
          // Update inventory (+1) since the order was cancelled
          if (orderDetails.listing_id) {
            try {
              await handleIncrementStock(orderDetails.listing_id, 1);
            } catch (stockError) {
              console.error('Exception during stock update:', stockError);
              // Continue with order cancellation even if stock update fails
            }
          }
        }
      }
      
      // Now update the order
      const { data, error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', orderId)
        .select('id, status, shipping_details');
      
      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }
      
      return { 
        id: orderId, 
        status: newStatus, 
        shippingDetails: data?.[0]?.shipping_details || null 
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['userSales', userId] });
      
      // Also invalidate purchases queries to update buyer's view
      queryClient.invalidateQueries({ queryKey: ['userPurchases'] });
      // Invalidate product queries to refresh stock
      queryClient.invalidateQueries({ queryKey: ['product'] });
      
      // Show success message
      toast({
        title: "Status përditësuar",
        description: `Porosia u përditësua në "${translateStatus(data.status)}" dhe blerësi u njoftua.`,
        variant: "default",
      });
      
      setIsUpdating(null);
      setCancelDialogOpen(false);
      setCancelOrderId(null);
      setCancellationReason('');
    },
    onError: (error: any) => {
      toast({
        title: "Gabim",
        description: error.message || "Ndodhi një gabim gjatë përditësimit të statusit",
        variant: "destructive",
      });
      
      setIsUpdating(null);
      setCancelDialogOpen(false);
      setCancelOrderId(null);
    },
  });
  
  // Handle cancellation confirmation
  const confirmCancellationMutation = useMutation({
    mutationFn: async (orderId: string) => {
      return await updateOrderStatusMutation.mutateAsync({ 
        orderId, 
        newStatus: 'cancelled' 
      });
    }
  });

  // Handle cancellation rejection - revert back to previous status
  const rejectCancellationMutation = useMutation({
    mutationFn: async (orderId: string) => {
      setIsUpdating(orderId);
      
      // Get the order to determine appropriate status to revert to
      const { data: order } = await supabase
        .from('orders')
        .select('shipping_details')
        .eq('id', orderId)
        .single();
      
      // Determine what status to revert to based on shipping details
      const revertStatus = order?.shipping_details ? 'shipped' : 'processing';
      
      // Update the order back to the previous status
      const { data, error } = await supabase
        .from('orders')
        .update({ 
          status: revertStatus, 
          requires_confirmation: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId)
        .select('id, status');
      
      if (error) throw error;
      
      return { id: orderId, status: revertStatus };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['userSales', userId] });
      queryClient.invalidateQueries({ queryKey: ['userPurchases'] });
      
      toast({
        title: "Kërkesa për anulim refuzuar",
        description: `Kërkesa për anulim u refuzua dhe porosia u kthye në statusin "${translateStatus(data.status)}".`,
        variant: "default",
      });
      
      setIsUpdating(null);
    },
    onError: (error: any) => {
      toast({
        title: "Gabim",
        description: error.message || "Ndodhi një gabim gjatë refuzimit të kërkesës për anulim",
        variant: "destructive",
      });
      
      setIsUpdating(null);
    },
  });

  // Helper to translate status to Albanian
  const translateStatus = (status: string): string => {
    const translations: { [key: string]: string } = {
      'pending': 'Në pritje',
      'processing': 'Duke u procesuar',
      'shipped': 'Dërguar',
      'delivered': 'Dorëzuar',
      'completed': 'Përfunduar',
      'cancelled': 'Anuluar'
    };
    
    return translations[status] || status;
  };
  
  // Handler for cancellation of orders by seller
  const handleCancelOrder = (orderId: string) => {
    setCancelOrderId(orderId);
    setCancelDialogOpen(true);
  };
  
  const confirmCancelOrder = () => {
    if (cancelOrderId && cancellationReason.trim()) {
      updateOrderStatusMutation.mutate({ 
        orderId: cancelOrderId,
        newStatus: 'cancelled'
      });
    } else {
      toast({
        title: "Gabim",
        description: "Ju lutemi shkruani arsyen e anulimit",
        variant: "destructive",
      });
    }
  };

  return {
    sales,
    isLoading,
    error,
    refetch,
    isUpdating,
    updateOrderStatus: (orderId: string, newStatus: string, shippingInfo?: ShippingInfo) => 
      updateOrderStatusMutation.mutate({ orderId, newStatus, shippingInfo }),
    confirmCancellation: (orderId: string) => confirmCancellationMutation.mutate(orderId),
    rejectCancellation: (orderId: string) => rejectCancellationMutation.mutate(orderId),
    handleCancelOrder,
    confirmCancelOrder,
    cancelDialogOpen,
    setCancelDialogOpen,
    cancellationReason,
    setCancellationReason,
  };
};
